import '../css/FeaturedGallery.less';

import _ from 'lodash';
import classNames from 'classnames';
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Link} from 'react-router-dom';
import * as queryString from 'query-string';
import WandbLoader from './WandbLoader';
import * as Url from '../util/url';
import * as Urls from '../util/urls';
import {useFeaturedReports} from '../util/reportGallery';
import LegacyWBIcon from './elements/LegacyWBIcon';
import {parseNameAndIDFromURL} from '../util/url';
import moment from 'moment';
import * as ViewerHooks from '../state/viewer/hooks';
import {auth} from '../setup';
import {
  useFeaturedReportsMetadataQuery,
  useStarViewMutation,
  useUnstarViewMutation,
  useUserReportsQuery,
  useSubmitReportForReviewMutation,
  useUpdateFeaturedReportsMutation,
  useFeaturedReportsAuthorsQuery,
} from '../generated/graphql';
import {DAY} from '../util/time';
import {
  Button,
  Dropdown,
  Icon,
  Divider,
  Modal,
  Input,
  TextArea,
  Form,
} from 'semantic-ui-react';
import WBModal from './WBModal';
import makeComp from '../util/profiler';
import ModifiedDropdown from './elements/ModifiedDropdown';
import {DropdownOption} from '../util/uihelpers';
import {toast} from './elements/Toast';
import {
  ExtraStuff,
  ExtraStuffSection,
  GalleryTag,
  GalleryTagSection,
  newExtraStuff,
  newGalleryTag,
  newGalleryTagSection,
  sortOrTagToPath,
  useTranslationLanguages,
  FeaturedReport,
  syncReportTags,
  useFormatGalleryTag,
  useSortOrTagToPath,
  useGalleryTagSections,
  useIsGalleryAdmin,
  TOP_CONTRIBUTORS_LABEL,
  useShowFreshnessPills,
} from '../util/gallery';
import produce from 'immer';
import {useDispatch} from '../state/hooks';
import {setGalleryTagSections} from '../state/global/actions';
import {Struct} from '../util/types';
import {ReportAuthor} from '../util/section';
import {useGalleryFreshnessContext} from '../util/userContext';
import EditableImage from './EditableImage';
import {
  setViewerUsingAdminPrivileges,
  viewerUsingAdminPrivileges,
} from '../util/admin';

type SortOrder = 'Latest' | 'Popular' | 'Trending';
const sortOrders: SortOrder[] = ['Latest', 'Popular', 'Trending'];

export interface GalleryReports {
  reports: FeaturedReport[];
  topLevelFeaturedReportIDs: string[];
  togglePinReport: (id: string) => void;
  activeTag: string;
  sortOrder: string;
}

type FeaturedReportWithID = FeaturedReport & {id: string};
type FeaturedReportWithMetadata = FeaturedReportWithID & {
  user?: {
    id: string;
    username?: string;
  };
  createdAt: string;
  updatedAt?: string;
  showcasedAt?: string;
  starCount: number;
  recentStarCount: number;
  starred?: boolean;
  discussionThreads: Struct;
};

const GalleryReports: React.FC<GalleryReports> = makeComp(
  ({
    reports,
    topLevelFeaturedReportIDs,
    togglePinReport,
    activeTag,
    sortOrder,
  }) => {
    const tagSections = useGalleryTagSections();
    const isTagSectionName = useCallback(
      (name: string) => tagSections.some(s => s.name === name),
      [tagSections]
    );
    const tagBelongsToSection = useMemo(() => {
      const memo: {[key: string]: boolean} = {};
      const fn = (sectionName: string, tagName: string) => {
        const memoKey = `${sectionName}/${tagName}`;
        if (memo[memoKey] != null) {
          return memo[memoKey];
        }
        const section = tagSections.find(s => s.name === sectionName);
        if (section == null) {
          memo[memoKey] = false;
          return false;
        }
        const res = section.tags.some(t => {
          if (t.name === tagName) {
            return true;
          }
          for (const subtag of t.subtags ?? []) {
            if (subtag.name === tagName) {
              return true;
            }
          }
          return false;
        });
        memo[memoKey] = res;
        return res;
      };
      return fn;
    }, [tagSections]);
    const langs = useTranslationLanguages();

    // this shouldn't change throughout the lifetime of the component
    const recentStarCountFrom = useMemo(
      () => new Date(Date.now() - 30 * DAY),
      []
    );

    // extra report info: report ID
    const reportsWithID: FeaturedReportWithID[] = reports.map(report => ({
      ...report,
      id: parseNameAndIDFromURL(report.link).id,
    }));
    let reportIDs = reportsWithID.map(report => report.id);

    // Query for extra report metadata
    const {
      loading: reportMetadataLoading,
      data: reportMetadata,
    } = useFeaturedReportsMetadataQuery({
      variables: {
        ids: reportIDs,
        recentStarCountFrom,
      },
    });

    // Query for author info
    let authors = _.compact(_.uniq(_.flatten(reports.map(r => r.authors))));
    const {
      loading: authorInfoLoading,
      data: authorInfo,
    } = useFeaturedReportsAuthorsQuery({
      variables: {
        usernames: authors,
      },
    });

    if (reportMetadataLoading || authorInfoLoading) {
      return (
        <div className="gallery-reports-section loading">
          <WandbLoader />
        </div>
      );
    }

    const reportViews = reportMetadata?.views?.edges?.map(e => e.node) ?? [];

    const authorMap: Struct<AuthorCardProps> = {};
    authorInfo?.users?.edges.forEach(e => {
      const author = e.node;
      if (author != null && author.username) {
        authorMap[author.username] = {
          username: author.username,
          name: author.name ?? '',
          reportCount: 0,
          bio: author.userInfo?.bio,
          company: author.userInfo?.company,
          location: author.userInfo?.location,
          photoUrl: author.photoUrl ?? '',
        };
      }
    });

    // Merge properties from reports and views into one collection of {id : merged-props} objects
    const featuredReportsWithoutMD: Struct<FeaturedReportWithID> = {};
    const featuredReports: Struct<FeaturedReportWithMetadata> = {};
    reportsWithID.forEach(report => {
      featuredReportsWithoutMD[report.id] = report;
    });
    reportViews.forEach(view => {
      if (view) {
        featuredReports[view.id] = ({
          ...featuredReportsWithoutMD[view.id],
          ...view,
        } as unknown) as FeaturedReportWithMetadata;
      }
    });
    reportIDs = reportIDs.filter(id => featuredReports[id] != null);

    // filter based on active tag
    if (activeTag !== '' && activeTag !== TOP_CONTRIBUTORS_LABEL) {
      if (isTagSectionName(activeTag)) {
        reportIDs = reportIDs.filter(rid => {
          const reportTags = featuredReports[rid].tags;
          return reportTags.some(t => tagBelongsToSection(activeTag, t));
        });
      } else {
        reportIDs = reportIDs.filter(rid => {
          const reportTags = featuredReports[rid].tags;
          return reportTags.includes(activeTag);
        });
      }
    }

    // filter international reports out unless a language is specified
    if (activeTag !== 'International' && !langs.includes(activeTag)) {
      const langSet = new Set(langs);
      reportIDs = reportIDs.filter(rid => {
        const reportTags = featuredReports[rid].tags;
        return !reportTags.some(t => langSet.has(t));
      });
    }

    let activeTagMetadata: GalleryTagSection | GalleryTag | null = null;
    if (activeTag) {
      outerLoop: for (const s of tagSections) {
        if (s.name === activeTag) {
          activeTagMetadata = s;
          break;
        }
        for (const t of s.tags) {
          if (t.name === activeTag) {
            activeTagMetadata = t;
            break outerLoop;
          }
          for (const st of t.subtags ?? []) {
            if (st.name === activeTag) {
              activeTagMetadata = st;
              break outerLoop;
            }
          }
        }
      }
    }

    let pinnedReportIDs: string[] = [];
    if (
      activeTagMetadata != null &&
      activeTagMetadata.featuredReportIDs != null &&
      activeTagMetadata.featuredReportIDs.length > 0
    ) {
      pinnedReportIDs = activeTagMetadata.featuredReportIDs;
    } else if (activeTag === '') {
      pinnedReportIDs = topLevelFeaturedReportIDs;
    }
    const pinnedReportIDSet = new Set<string>(pinnedReportIDs);

    const normalReportIDs: string[] = [];
    reportIDs.forEach(rid => {
      if (!pinnedReportIDSet.has(rid)) {
        normalReportIDs.push(rid);
      }
    });

    let featuredReportsSorted: FeaturedReportWithMetadata[] = [];
    normalReportIDs.forEach(rid =>
      featuredReportsSorted.push(featuredReports[rid])
    );

    function sortGallery(order: string) {
      switch (order) {
        case 'Popular':
          return _.sortBy(featuredReportsSorted, [
            r => {
              const sc = r.starCount;
              return -sc;
            },
          ]);
        case 'Trending':
          return _.sortBy(featuredReportsSorted, [
            r => {
              const rsc = r.recentStarCount;
              return -rsc;
            },
          ]);
        case 'Latest':
        default:
          return _.sortBy(featuredReportsSorted, [
            r => {
              const d = new Date(r.addedAt).valueOf();
              return -d;
            },
          ]);
      }
    }

    featuredReportsSorted = sortGallery(sortOrder);

    // Increment user's report count for each report authored
    reportIDs.forEach(rid => {
      const report = featuredReports[rid];
      report.authors.forEach(a => {
        authorMap[a].reportCount += 1;
      });
    });
    // Score authors by number of gallery contributions
    authors = _.sortBy(authors, [
      a => {
        return -authorMap[a].reportCount;
      },
    ]);

    return activeTag === TOP_CONTRIBUTORS_LABEL ? (
      <div className="gallery-reports-section">
        <div
          className="gallery-reports-section-header"
          style={{borderBottom: 'none'}}>
          <p>{TOP_CONTRIBUTORS_LABEL}</p>
          <div className="subheader">
            Machine learning practitioners who have written the most featured
            reports
          </div>
        </div>
        {authors.map(a => (
          <AuthorCard {...authorMap[a]} key={authorMap[a].username} />
        ))}
      </div>
    ) : (
      <div className="gallery-reports-section">
        {!activeTag && (
          <div className="gallery-reports-section-intro">
            Discover curated machine learning tools, best practices, research &
            advice.
            <br />
            Showcase your work, share ideas, and find collaborators.
          </div>
        )}
        {activeTagMetadata?.description && activeTagMetadata?.imageURL && (
          <>
            <div className="gallery-reports-section-header tag-description-header">
              <p>{activeTagMetadata.name}</p>
            </div>
            <div className="featured-report-card tag-description">
              <div className="report-info">
                <div className="report-text">
                  {activeTagMetadata.description}
                </div>
                <div className="report-image">
                  <img
                    alt={activeTagMetadata.name}
                    src={activeTagMetadata.imageURL}
                  />
                </div>
              </div>
            </div>
          </>
        )}
        {reportIDs.length === 0 && (
          <div className="gallery-no-reports">
            <p>{`No features are currently tagged "${activeTag}"`}</p>
            <p>Message growth@wandb.com to request a report on this topic.</p>
          </div>
        )}
        {pinnedReportIDs.length > 0 && (
          <>
            <div className="gallery-reports-section-header">
              <p>Today's featured reports</p>
            </div>
            {pinnedReportIDs.map(rid => {
              const report = featuredReports[rid];
              return (
                <FeaturedReportCard
                  {...report}
                  key={report.id}
                  isPinned
                  onTogglePinned={() => togglePinReport(report.id)}
                  activeTag={activeTag}
                  authorInfo={report.authors.map(a => ({
                    name: authorMap[a].name,
                    username: a,
                  }))}
                />
              );
            })}
          </>
        )}
        {normalReportIDs.length !== 0 && (
          <>
            <div className="gallery-reports-section-header">
              <div className="sort-report">
                <Dropdown icon="wbic-ic-down" text={`${sortOrder} reports`}>
                  <Dropdown.Menu>
                    {sortOrders.map(order => (
                      <Link
                        key={order}
                        to={galleryPath(tagSections, activeTag, order)}>
                        <Dropdown.Item>{order}</Dropdown.Item>
                      </Link>
                    ))}
                  </Dropdown.Menu>
                </Dropdown>
              </div>
            </div>
          </>
        )}
        {featuredReportsSorted.map(report => (
          <FeaturedReportCard
            {...report}
            key={report.id}
            onTogglePinned={() => togglePinReport(report.id)}
            activeTag={activeTag}
            authorInfo={report.authors.map(a => ({
              name: authorMap[a].name,
              username: a,
            }))}
          />
        ))}
      </div>
    );
  },
  {id: 'GalleryReports'}
);

interface AuthorCardProps {
  username: string;
  name: string;
  reportCount: number;
  bio?: string;
  company?: string;
  location?: string;
  photoUrl?: string;
}

export const AuthorCard: React.FC<AuthorCardProps> = makeComp(
  props => {
    const {
      username,
      name,
      reportCount,
      bio,
      company,
      location,
      photoUrl,
    } = props;
    const authorInfo = [];
    const bioTrimmed = bio ? bio.trim() : '';
    const companyTrimmed = company ? company.trim() : '';
    const locationTrimmed = location ? location.trim() : '';

    if (bioTrimmed !== '') {
      // Hardcoded bio cutoff for now. It's really ugly.
      // css is tricky for line wrapping combined with
      // conditional ellipsis overflow based on sibling elements.
      // TODO(aditya): find a better way to dynamically do this
      const MAX_AUTHOR_INFO_CHAR_COUNT = 130;
      const SEPARATOR_CHAR_COUNT = 3;

      let cutoff = MAX_AUTHOR_INFO_CHAR_COUNT;
      if (companyTrimmed !== '') {
        cutoff -= companyTrimmed.length + SEPARATOR_CHAR_COUNT;
      }
      if (locationTrimmed !== '') {
        cutoff -= locationTrimmed.length + SEPARATOR_CHAR_COUNT;
      }

      authorInfo.push(
        bioTrimmed.length > cutoff
          ? bioTrimmed.slice(0, cutoff - 3) + '...'
          : bio
      );
    }

    if (companyTrimmed !== '') {
      authorInfo.push(companyTrimmed);
    }

    if (locationTrimmed !== '') {
      authorInfo.push(locationTrimmed);
    }

    return (
      <Link to={`/${username}`} className="author-card">
        <div className="author-report-count">{reportCount}</div>
        <div className="author-profile-pic">
          <img alt="" src={photoUrl} />
        </div>
        <div className="author-info">
          <div className="author-name">{name}</div>
          <div className="author-bio">{authorInfo.join(' • ')}</div>
        </div>
      </Link>
    );
  },
  {id: 'AuthorCard'}
);

type FeaturedReportCardRequiredFields =
  | 'id'
  | 'author'
  | 'description'
  | 'imageURL'
  | 'link'
  | 'title'
  | 'tags';
type FeaturedReportCardProps = Pick<
  FeaturedReportWithMetadata,
  FeaturedReportCardRequiredFields
> &
  Partial<
    Omit<FeaturedReportWithMetadata, FeaturedReportCardRequiredFields>
  > & {
    authorInfo: ReportAuthor[];
    activeTag?: string;
    isPinned?: true;
    onTogglePinned?: () => void;
  };

export const FeaturedReportCard: React.FC<FeaturedReportCardProps> = makeComp(
  props => {
    const admin = useIsGalleryAdmin();
    const [starView] = useStarViewMutation();
    const [unstarView] = useUnstarViewMutation();

    const starReport = (id: string) => starView({variables: {id}});
    const unstarReport = (id: string) => unstarView({variables: {id}});

    const toggleStarred = () =>
      props.starred ? unstarReport(props.id) : starReport(props.id);

    const viewer = ViewerHooks.useViewer();

    // Unfortunately google doesn't like non-url safe strings in urls
    // this undoes the old unsafe urls in spectacular hack on hack fashion
    let link = props.link;
    const linkParts = props.link.split('/');
    const slug = linkParts.pop();
    if (slug != null) {
      link = linkParts.concat([Url.urlSafeSlug(slug)]).join('/');
    }

    let commentCount = 0;
    props.discussionThreads?.edges.forEach(
      (dt: Struct) => (commentCount += dt.node.comments.totalCount)
    );

    const activeTagPath = useSortOrTagToPath(props.activeTag ?? '');
    if (props.activeTag) {
      const parsed = queryString.parseUrl(link);
      link = queryString.stringifyUrl({
        url: parsed.url,
        query: {...parsed.query, galleryTag: activeTagPath},
      });
    }

    return (
      <Link to={link} title={props.title} className="featured-report-card">
        <div className="report-actions">
          <div className="report-action">
            {moment(props.addedAt)
              .format('MMM D, YYYY')
              .toUpperCase()}
          </div>
          <div
            className="report-action report-action__button"
            onClick={e => {
              e.preventDefault();
              if (viewer == null) {
                auth.login();
                return;
              }
              toggleStarred();
            }}>
            <LegacyWBIcon name={props.starred ? 'heart' : 'heart-outline'} />
            {props.starCount ?? 0}
          </div>
          <Link
            to={link + '#comments'}
            className="report-action report-action__button">
            <LegacyWBIcon name="chat" />
            {commentCount}
          </Link>
          {admin && props.onTogglePinned != null && (
            <div
              className="pin-report"
              onClick={e => {
                e.preventDefault();
                props.onTogglePinned?.();
              }}>
              {props.isPinned ? 'Unpin' : 'Pin'}
            </div>
          )}
        </div>
        <div className="report-info">
          <div className="report-text">
            <p className="title">{props.title}</p>
            <div className="author-container">
              {props.authorInfo?.map((a, i) => (
                <React.Fragment key={a.username}>
                  <Link className="author" title={a.name} to={`/${a.username}`}>
                    {a.name}
                  </Link>
                  {/* Add comma separator between author names */}
                  {i < props.authorInfo.length - 1 && <span>,&nbsp;</span>}
                </React.Fragment>
              ))}
            </div>
            <ReportCardTagList tags={props.tags} />
          </div>
          <div className="report-image">
            <img alt="" src={props.imageURL} />
          </div>
        </div>
      </Link>
    );
  },
  {id: 'FeaturedReportCard'}
);

interface FeaturedGalleryProps {
  renderSidebars?: boolean;
  match: {
    params: {
      sortOrTag?: string;
      tag?: string;
    };
  };
}

function galleryPath(
  tagSections: readonly GalleryTagSection[],
  activeTag: string,
  sortOrder: SortOrder,
  hash: string = ''
) {
  if (sortOrder === 'Latest') {
    if (activeTag === '') {
      return '/gallery' + hash;
    } else {
      return `/gallery/${sortOrTagToPath(tagSections, activeTag)}${hash}`;
    }
  } else {
    if (activeTag === '') {
      return `/gallery/${sortOrTagToPath(tagSections, sortOrder)}${hash}`;
    } else {
      return `/gallery/${sortOrTagToPath(
        tagSections,
        sortOrder
      )}/${sortOrTagToPath(tagSections, activeTag)}${hash}`;
    }
  }
}

const FeaturedGallery: React.FC<FeaturedGalleryProps> = makeComp(
  ({renderSidebars = false, match: {params}}) => {
    const dispatch = useDispatch();
    const formattedSortOrTag = useFormatGalleryTag(params.sortOrTag ?? '');
    const formattedTag = useFormatGalleryTag(params.tag ?? '');
    const {
      loading,
      viewID,
      reports: serverReports,
      tagSections: serverTagSections,
      extraStuffSections: serverExtraStuffSections,
      topLevelFeaturedReportIDs: serverTopLevelFeaturedReportIDs,
    } = useFeaturedReports();

    const viewer = ViewerHooks.useViewer();
    const admin = useIsGalleryAdmin();

    const [menuOpen, setMenuOpen] = useState(false);
    const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
    const [leftSidebarOpen, setLeftSidebarOpen] = useState(false);

    const [activeTag, setActiveTag] = useState('');
    const [sortOrder, setSortOrder] = useState<SortOrder>('Latest');

    const [reports, setReports] = useState<FeaturedReport[]>(serverReports);
    const tagSections = useGalleryTagSections();
    const setTagSectionsAndSync = useCallback(
      (ts: GalleryTagSection[], change?: TagChange) => {
        dispatch(setGalleryTagSections(ts));

        if (change != null && change.before.name !== change.after.name) {
          const before = change.before.name;
          const after = change.after.name;
          setReports(prev => syncReportTags(prev, before, after));
          setActiveTag(prev => (prev === before ? after : prev));
        }
      },
      [dispatch]
    );
    const [extraStuffSections, setExtraStuffSections] = useState(
      serverExtraStuffSections
    );
    const [topLevelFeaturedReportIDs, setTopLevelFeaturedReportIDs] = useState(
      serverTopLevelFeaturedReportIDs
    );
    useLayoutEffect(() => {
      setReports(serverReports);
    }, [serverReports]);
    useLayoutEffect(() => {
      if (serverExtraStuffSections != null) {
        setExtraStuffSections(serverExtraStuffSections);
      }
    }, [serverExtraStuffSections]);
    useLayoutEffect(() => {
      if (serverTopLevelFeaturedReportIDs != null) {
        setTopLevelFeaturedReportIDs(serverTopLevelFeaturedReportIDs);
      }
    }, [serverTopLevelFeaturedReportIDs]);

    const unsavedEdits =
      !loading &&
      viewID != null &&
      !(
        _.isEqual(serverTagSections, tagSections) &&
        _.isEqual(serverExtraStuffSections, extraStuffSections) &&
        _.isEqual(serverTopLevelFeaturedReportIDs, topLevelFeaturedReportIDs)
      );

    const [updateFeaturedReports] = useUpdateFeaturedReportsMutation();
    const [savingEdits, setSavingEdits] = useState(false);
    const saveEdits = async () => {
      if (viewID == null || !admin || !unsavedEdits) {
        return;
      }
      setSavingEdits(true);

      try {
        const wasUsingAdmin = viewerUsingAdminPrivileges();
        setViewerUsingAdminPrivileges(true);
        await updateFeaturedReports({
          variables: {
            id: viewID,
            spec: JSON.stringify({
              reports,
              tagSections,
              extraStuffSections,
              topLevelFeaturedReportIDs,
            }),
          },
        });
        setViewerUsingAdminPrivileges(wasUsingAdmin);
      } catch (e) {
        alert(`Error updating gallery: ${e.message}`);
        console.error(`Error updating gallery: ${e}`);
      } finally {
        setSavingEdits(false);
      }
    };

    /*
     * Set up report submission modal
     */
    const userReportsQuery = useUserReportsQuery();
    const [submitReportForReview] = useSubmitReportForReviewMutation();

    const [submitModalOpen, setSubmitModalOpen] = useState(false);
    const onSubmitModalOpen = useCallback(() => setSubmitModalOpen(true), []);
    const onSubmitModalClose = useCallback(() => setSubmitModalOpen(false), []);

    const [selectedReportID, setSelectedReportID] = useState('');
    const onChangeReportID = useCallback((e, data) => {
      setSelectedReportID(data.value);
    }, []);
    const resetSelectedReportID = useCallback(
      () => setSelectedReportID(''),
      []
    );

    const reportOptions: DropdownOption[] = useMemo(() => {
      if (userReportsQuery.data == null) {
        return [];
      }
      const userReports = userReportsQuery.data.viewer?.views?.edges ?? [];
      const opts: DropdownOption[] = [];
      for (const r of userReports) {
        const entityName = r.node?.entity
          ? r.node.entity.name
          : userReportsQuery.data.viewer?.username ?? '';
        const projectName = r.node?.project ? r.node.project.name : '';
        const reportName = r.node?.displayName ?? '';
        const reportID = r.node?.id ?? '';
        opts.push({
          key: reportID,
          value: reportID,
          text: [entityName, projectName, reportName].join(' / '),
        });
      }
      return opts;
    }, [userReportsQuery]);

    // Set state based on url
    useEffect(() => {
      // Extract mobile browser menu state
      const menuOpenHash = window.location.hash === '#menu';
      const leftSidebarOpenHash = window.location.hash === '#topics';
      const rightSidebarOpenHash = window.location.hash === '#events';
      let computedActiveTag = '';
      let computedSortOrder: SortOrder = 'Latest';
      if (params.sortOrTag != null) {
        switch (params.sortOrTag) {
          case 'latest':
            computedSortOrder = 'Latest';
            break;
          case 'popular':
            computedSortOrder = 'Popular';
            break;
          case 'trending':
            computedSortOrder = 'Trending';
            break;
          default:
            // Not a sort order, but a tag instead
            computedActiveTag = formattedSortOrTag;
        }
      }
      if (params.tag != null) {
        computedActiveTag = formattedTag;
      }
      setMenuOpen(menuOpenHash);
      setLeftSidebarOpen(leftSidebarOpenHash);
      setRightSidebarOpen(rightSidebarOpenHash);
      setActiveTag(computedActiveTag);
      setSortOrder(computedSortOrder);
    }, [params, formattedSortOrTag, formattedTag]);

    if (loading) {
      return (
        <div style={{minHeight: 500}}>
          <WandbLoader />
        </div>
      );
    }

    return (
      <>
        {unsavedEdits && admin && (
          <div className="save-gallery-edits">
            You have unsaved changes to the gallery.
            <Button
              primary
              disabled={savingEdits}
              loading={savingEdits}
              onClick={saveEdits}>
              Save
            </Button>
          </div>
        )}
        <div className={`gallery-nav-container`}>
          <div className="gallery-nav">
            <div className="gallery-nav-header">Gallery</div>
            <Link
              to={
                window.location.hash === '#menu'
                  ? galleryPath(tagSections, activeTag, sortOrder)
                  : galleryPath(tagSections, activeTag, sortOrder, '#menu')
              }>
              <Icon name="sidebar" />
            </Link>
          </div>
          <div className={`gallery-nav-content ${menuOpen ? 'open' : ''}`}>
            <Link
              to={galleryPath(tagSections, activeTag, sortOrder, '#topics')}
              className="gallery-nav-content-item">
              Report topics
            </Link>
            <Divider />
            <Link
              to={galleryPath(tagSections, activeTag, sortOrder, '#events')}
              className="gallery-nav-content-item">
              Upcoming events
            </Link>
          </div>
        </div>
        <div className="gallery">
          {renderSidebars && (
            <div
              className={`gallery-sidebar left ${
                leftSidebarOpen ? 'open' : ''
              }`}>
              <Link
                to={galleryPath(tagSections, activeTag, sortOrder)}
                className="sidebar-nav">
                <LegacyWBIcon name="down" />
                Report topics
              </Link>
              <div className="gallery-sidebar-content">
                <h1>Gallery</h1>
                <h3>An MLOps community</h3>
                <TagList
                  reports={reports}
                  sections={tagSections}
                  activeTag={activeTag}
                  sortOrder={sortOrder}
                  onSetSections={setTagSectionsAndSync}
                />
              </div>
            </div>
          )}
          <GalleryReports
            reports={reports}
            topLevelFeaturedReportIDs={topLevelFeaturedReportIDs}
            togglePinReport={(id: string) => {
              if (activeTag === '') {
                setTopLevelFeaturedReportIDs(
                  produce(topLevelFeaturedReportIDs, draft => {
                    const i = draft.findIndex(featuredID => featuredID === id);
                    if (i > -1) {
                      draft.splice(i, 1);
                    } else {
                      draft.push(id);
                    }
                  })
                );
                return;
              }
              setTagSectionsAndSync(
                produce(tagSections, draft => {
                  for (const s of draft) {
                    if (s.name === activeTag) {
                      s.featuredReportIDs = s.featuredReportIDs ?? [];
                      const i = s.featuredReportIDs.findIndex(
                        featuredID => featuredID === id
                      );
                      if (i > -1) {
                        s.featuredReportIDs.splice(i, 1);
                      } else {
                        s.featuredReportIDs.push(id);
                      }
                      break;
                    }
                    for (const t of s.tags) {
                      if (t.name === activeTag) {
                        t.featuredReportIDs = t.featuredReportIDs ?? [];
                        const i = t.featuredReportIDs.findIndex(
                          featuredID => featuredID === id
                        );
                        if (i > -1) {
                          t.featuredReportIDs.splice(i, 1);
                        } else {
                          t.featuredReportIDs.push(id);
                        }
                        break;
                      }
                    }
                  }
                }) as GalleryTagSection[]
              );
            }}
            activeTag={activeTag}
            sortOrder={sortOrder}
          />
          {renderSidebars && (
            <div
              className={`gallery-sidebar right ${
                rightSidebarOpen ? 'open' : ''
              }`}>
              <Link
                to={galleryPath(tagSections, activeTag, sortOrder)}
                className="sidebar-nav">
                <LegacyWBIcon name="down" />
                Upcoming events
              </Link>
              <div className="gallery-sidebar-content">
                <Button
                  content="Submit to gallery"
                  className="gallery-sidebar-button submit-to-gallery"
                  onClick={() => {
                    if (viewer == null) {
                      auth.login();
                      return;
                    }
                    onSubmitModalOpen();
                  }}
                />
                <Button
                  href={Urls.slack()}
                  className="gallery-sidebar-button community-forum">
                  ML Community
                </Button>
                <ExtraStuffList
                  sections={extraStuffSections}
                  onSetSections={setExtraStuffSections}
                />
              </div>
            </div>
          )}
        </div>
        <WBModal
          open={submitModalOpen}
          onClose={onSubmitModalClose}
          header="Submit a report"
          primaryAction={
            reportOptions.length > 0
              ? {
                  content: 'Submit',
                  onClick: () => {
                    if (selectedReportID === '') {
                      toast('Please select a report');
                    } else {
                      onSubmitModalClose();
                      toast(
                        'Thank you for your submission to the Gallery! Our team will get back to your within 24 hours.'
                      );
                      submitReportForReview({
                        variables: {
                          reportID: selectedReportID,
                        },
                      });
                      resetSelectedReportID();
                    }
                  },
                }
              : {content: 'Close', onClick: onSubmitModalClose}
          }>
          {reportOptions.length > 0 ? (
            <>
              <p>
                Pick a report to submit to the featured gallery. We’ll review
                your submission and contact you with any suggestions before
                publishing.
              </p>
              <ModifiedDropdown
                selection
                search
                style={{width: '100%'}}
                placeholder="Select a report"
                options={reportOptions}
                onChange={onChangeReportID}
              />
            </>
          ) : (
            <p>
              Create a report in one of your projects to visualize data logged
              to W&B and share findings.
            </p>
          )}
        </WBModal>
      </>
    );
  },
  {id: 'FeaturedGallery'}
);
export default FeaturedGallery;

interface TagChange {
  before: GalleryTag;
  after: GalleryTag;
}

interface TagListProps {
  reports: FeaturedReport[];
  sections: readonly GalleryTagSection[];
  activeTag: string;
  sortOrder: SortOrder;
  onSetSections(
    sections: readonly GalleryTagSection[],
    change?: TagChange
  ): void;
}

const TagList: React.FC<TagListProps> = makeComp(
  ({reports, sections, activeTag, sortOrder, onSetSections}) => {
    const tagSections = useGalleryTagSections();
    const sectionNamesByTagName: {[t: string]: string} = useMemo(() => {
      const res: {[t: string]: string} = {};
      for (const s of tagSections) {
        for (const t of s.tags) {
          res[t.name] = s.name;
          for (const st of t.subtags ?? []) {
            res[st.name] = s.name;
          }
        }
      }
      return res;
    }, [tagSections]);

    const [expandedTags, setExpandedTags] = useState<{[tagName: string]: true}>(
      {}
    );
    const toggleExpandTag = useCallback((tagName: string) => {
      setExpandedTags(prev =>
        produce(prev, draft => {
          if (draft[tagName]) {
            delete draft[tagName];
          } else {
            draft[tagName] = true;
          }
        })
      );
    }, []);
    useEffect(() => {
      // we should always expand a parent tag with an active subtag
      for (const s of tagSections) {
        for (const t of s.tags) {
          for (const st of t.subtags ?? []) {
            if (st.name === activeTag && !expandedTags[t.name]) {
              toggleExpandTag(t.name);
            }
          }
        }
      }
    }, [tagSections, activeTag, expandedTags, toggleExpandTag]);

    const {
      state: freshness,
      setState: setFreshness,
      loading: freshnessLoading,
    } = useGalleryFreshnessContext();
    const showFreshnessPills = useShowFreshnessPills() || !_.isEmpty(freshness);
    const newPostsByTagKey: {[key: string]: number} = useMemo(() => {
      const res: {[key: string]: number} = {};
      if (freshnessLoading) {
        return res;
      }
      for (const r of reports) {
        for (const t of r.tags) {
          const sectionName = sectionNamesByTagName[t];
          if (sectionName == null) {
            continue;
          }
          const key = `${sectionName}/${t}`;
          const f = freshness[key];
          if (f == null || r.addedAt > f) {
            res[key] = res[key] ?? 0;
            res[key]++;
          }
        }
      }
      return res;
    }, [freshnessLoading, freshness, reports, sectionNamesByTagName]);
    useEffect(() => {
      const newFreshness = produce(freshness, draft => {
        const sectionName = sectionNamesByTagName[activeTag];
        if (sectionName == null) {
          return;
        }
        const key = `${sectionName}/${activeTag}`;
        draft[key] = Date.now();
      });
      setFreshness(newFreshness);
      // eslint-disable-next-line
    }, [freshnessLoading, activeTag]);

    const [
      editingSection,
      setEditingSection,
    ] = useState<GalleryTagSection | null>(null);
    const [editingSectionIndex, setEditingSectionIndex] = useState<
      number | null
    >(null);
    const [addingSection, setAddingSection] = useState(false);
    const editingSectionValid =
      editingSection != null && editingSection.name.length > 0;

    const [editingStuff, setEditingStuff] = useState<GalleryTag | null>(null);
    const [editingStuffIndices, setEditingStuffIndices] = useState<
      number[] | null
    >(null);
    const editingStuffValid =
      editingStuff != null
        ? Boolean(editingStuff.name && editingStuff.linkForm)
        : false;

    const admin = useIsGalleryAdmin();

    const editSection = (prop: keyof GalleryTagSection, val: string) =>
      setEditingSection(section => {
        if (section == null) {
          return null;
        }
        return produce(section, draft => {
          (draft as Struct)[prop] = val;
        });
      });
    const editStuff = (prop: keyof GalleryTag, val: string) =>
      setEditingStuff(stuff => {
        if (stuff == null) {
          return null;
        }
        return produce(stuff, draft => {
          (draft as Struct)[prop] = val;
        });
      });

    const startEditSection = (
      section: GalleryTagSection,
      i: number,
      adding = false
    ) => {
      setEditingSection(section);
      setEditingSectionIndex(i);
      setAddingSection(adding);
    };
    const stopEditSection = () => {
      setEditingSection(null);
      setEditingSectionIndex(null);
      setAddingSection(false);
    };
    const startEditStuff = (stuff: GalleryTag, is: number[]) => {
      setEditingStuff(stuff);
      setEditingStuffIndices(is);
    };
    const stopEditStuff = () => {
      setEditingStuff(null);
      setEditingStuffIndices(null);
    };

    const moveSectionUp = (i: number) => {
      if (i === 0) {
        return;
      }
      onSetSections(
        produce(sections, draft => {
          const [section] = draft.splice(i, 1);
          draft.splice(i - 1, 0, section);
        })
      );
    };
    const moveSectionDown = (i: number) => {
      if (i === sections.length - 1) {
        return;
      }
      onSetSections(
        produce(sections, draft => {
          const [section] = draft.splice(i, 1);
          draft.splice(i + 1, 0, section);
        })
      );
    };
    const moveStuffUp = ([i, j, k]: number[]) => {
      if (k != null && sections[i].tags[j].subtags != null) {
        if (k === 0) {
          return;
        }
        onSetSections(
          produce(sections, draft => {
            const section = draft[i];
            const parentTag = section.tags[j];
            if (parentTag.subtags == null) {
              return;
            }
            const [item] = parentTag.subtags.splice(k, 1);
            parentTag.subtags.splice(k - 1, 0, item);
          })
        );
        return;
      }
      if (j === 0) {
        return;
      }
      onSetSections(
        produce(sections, draft => {
          const section = draft[i];
          const [item] = section.tags.splice(j, 1);
          section.tags.splice(j - 1, 0, item);
        })
      );
    };
    const moveStuffDown = ([i, j, k]: number[]) => {
      if (k != null && sections[i].tags[j].subtags != null) {
        if (k === sections[i].tags[j].subtags!.length - 1) {
          return;
        }
        onSetSections(
          produce(sections, draft => {
            const section = draft[i];
            const parentTag = section.tags[j];
            if (parentTag.subtags == null) {
              return;
            }
            const [item] = parentTag.subtags.splice(k, 1);
            parentTag.subtags.splice(k + 1, 0, item);
          })
        );
        return;
      }
      if (j === sections[i].tags.length - 1) {
        return;
      }
      onSetSections(
        produce(sections, draft => {
          const section = draft[i];
          const [item] = section.tags.splice(j, 1);
          section.tags.splice(j + 1, 0, item);
        })
      );
    };

    const saveSection = () => {
      if (editingSection == null || editingSectionIndex == null) {
        return;
      }
      const i = editingSectionIndex;

      const newSections = produce(sections, draft => {
        if (addingSection) {
          draft.splice(i, 0, editingSection);
        } else {
          draft[i] = editingSection;
        }
      });
      onSetSections(newSections);

      stopEditSection();
    };
    const saveStuff = () => {
      if (editingStuff == null || editingStuffIndices == null) {
        return;
      }
      const [i, j, k] = editingStuffIndices;

      if (k != null) {
        let firstSubtagCreated = false;
        const newSections = produce(sections, draft => {
          const section = draft[i];
          const parentTag = section.tags[j];
          if (k === -1) {
            parentTag.subtags = parentTag.subtags ?? [];
            parentTag.subtags.push(editingStuff);
            if (parentTag.subtags.length === 1) {
              firstSubtagCreated = true;
            }
          } else if (parentTag.subtags != null) {
            parentTag.subtags[k] = editingStuff;
          }
        });
        const tagChange =
          k > -1
            ? {
                before: sections[i].tags[j].subtags![k],
                after: newSections[i].tags[j].subtags![k],
              }
            : undefined;
        onSetSections(newSections, tagChange);
        if (firstSubtagCreated) {
          toggleExpandTag(newSections[i].tags[j].name);
        }
      } else {
        const newSections = produce(sections, draft => {
          const section = draft[i];
          if (j === -1) {
            section.tags.push(editingStuff);
          } else {
            section.tags[j] = editingStuff;
          }
        });
        const tagChange =
          j > -1
            ? {
                before: sections[i].tags[j],
                after: newSections[i].tags[j],
              }
            : undefined;
        onSetSections(newSections, tagChange);
      }
      stopEditStuff();
    };

    const deleteSection = (i: number) => {
      onSetSections(
        produce(sections, draft => {
          draft.splice(i, 1);
        })
      );
    };
    const deleteStuff = () => {
      if (
        editingStuff == null ||
        editingStuffIndices == null ||
        editingStuffIndices[1] === -1
      ) {
        return;
      }
      const [i, j, k] = editingStuffIndices;

      onSetSections(
        produce(sections, draft => {
          const section = draft[i];
          if (k != null) {
            const parentTag = section.tags[j];
            parentTag.subtags!.splice(k, 1);
            if (parentTag.subtags!.length === 0) {
              delete parentTag.subtags;
            }
          } else {
            section.tags.splice(j, 1);
          }
        })
      );

      stopEditStuff();
    };
    return (
      <div className="tag-list">
        {editingSection != null && (
          <Modal open onClose={stopEditSection} className="edit-event-modal">
            <Modal.Content>
              <Form>
                <div className="edit-event-modal-field">
                  <div className="edit-event-modal-label">Name</div>
                  <Input
                    className="edit-event-modal-input"
                    value={editingSection.name}
                    onChange={(e, {value}) => editSection('name', value)}
                  />
                </div>
                <div className="edit-event-modal-field">
                  <div className="edit-event-modal-label">Description</div>
                  <TextArea
                    className="edit-event-modal-input"
                    value={editingSection.description ?? ''}
                    onChange={(e, {value}) =>
                      editSection('description', value as string)
                    }
                  />
                </div>
                <div className="edit-event-modal-field">
                  <div className="edit-event-modal-label">Image</div>
                  <EditableImage
                    photoUrl={editingSection.imageURL ?? '/wave.gif'}
                    displaySize={200}
                    label={false}
                    save={value => editSection('imageURL', value)}
                  />
                </div>
              </Form>
            </Modal.Content>
            <Modal.Actions>
              <Button onClick={stopEditSection}>Cancel</Button>
              <Button
                primary
                disabled={!editingSectionValid}
                onClick={saveSection}>
                Confirm
              </Button>
            </Modal.Actions>
          </Modal>
        )}
        {editingStuff != null && (
          <Modal open onClose={stopEditStuff} className="edit-event-modal">
            <Modal.Content>
              <div className="edit-event-modal-warning">
                WARNING: Editing a tag's name and/or link form may break
                existing links for and/or affect analytics pipelines tracking
                that tag.
              </div>
              <Form>
                <div className="edit-event-modal-field">
                  <div className="edit-event-modal-label">Name</div>
                  <Input
                    className="edit-event-modal-input"
                    value={editingStuff.name}
                    onChange={(e, {value}) =>
                      editStuff('name', value as string)
                    }
                  />
                </div>
                <div className="edit-event-modal-field">
                  <div className="edit-event-modal-label">Description</div>
                  <TextArea
                    className="edit-event-modal-input"
                    value={editingStuff.description ?? ''}
                    onChange={(e, {value}) =>
                      editStuff('description', value as string)
                    }
                  />
                </div>
                <div className="edit-event-modal-field">
                  <div className="edit-event-modal-label">Image</div>
                  <EditableImage
                    photoUrl={editingStuff.imageURL ?? '/wave.gif'}
                    displaySize={200}
                    label={false}
                    save={value => editStuff('imageURL', value)}
                  />
                </div>
                <div className="edit-event-modal-field">
                  <div className="edit-event-modal-label">Link Form</div>
                  <Input
                    className="edit-event-modal-input"
                    value={editingStuff.linkForm}
                    onChange={(e, {value}) => editStuff('linkForm', value)}
                  />
                  <div className="edit-event-modal-subtext">
                    {window.location.origin}/gallery/
                    {editingStuff.linkForm || '{Link Form}'}
                  </div>
                </div>
              </Form>
            </Modal.Content>
            <Modal.Actions>
              {editingStuffIndices?.[1] !== -1 && (
                <Button
                  className="edit-event-modal-delete"
                  onClick={deleteStuff}>
                  Delete
                </Button>
              )}
              <Button onClick={stopEditStuff}>Cancel</Button>
              <Button primary disabled={!editingStuffValid} onClick={saveStuff}>
                Confirm
              </Button>
            </Modal.Actions>
          </Modal>
        )}
        <div className="tag-list-section">
          <div
            className={classNames('tag-list-item', {active: activeTag === ''})}>
            <Link to={galleryPath(tagSections, '', sortOrder)}>
              All reports
            </Link>
          </div>
          <div
            className={classNames('tag-list-item', {
              active: activeTag === TOP_CONTRIBUTORS_LABEL,
            })}>
            <Link
              to={galleryPath(tagSections, TOP_CONTRIBUTORS_LABEL, sortOrder)}>
              {TOP_CONTRIBUTORS_LABEL}
            </Link>
          </div>
        </div>
        {sections.map((s, secI) => (
          <React.Fragment key={s.name}>
            {admin && (
              <div className="add-section">
                <LegacyWBIcon
                  className="edit-stuff"
                  name="plus"
                  onClick={() =>
                    startEditSection(newGalleryTagSection(), secI, true)
                  }
                />
              </div>
            )}
            <div className="tag-list-section">
              <div
                className={classNames('tag-list-header', {
                  active: activeTag === s.name,
                })}>
                <Link to={galleryPath(tagSections, s.name, sortOrder)}>
                  {s.name}
                </Link>
                {admin && (
                  <>
                    <LegacyWBIcon
                      className="delete-section"
                      name="close"
                      onClick={() => deleteSection(secI)}
                    />
                    <LegacyWBIcon
                      className="edit-stuff add-tag"
                      name="plus"
                      onClick={() =>
                        startEditStuff(newGalleryTag(), [secI, -1])
                      }
                    />
                    {secI > 0 && (
                      <LegacyWBIcon
                        className="edit-stuff move-up"
                        name="up-arrow"
                        onClick={() => moveSectionUp(secI)}
                      />
                    )}
                    {secI < sections.length - 1 && (
                      <LegacyWBIcon
                        className="edit-stuff move-down"
                        name="down-arrow"
                        onClick={() => moveSectionDown(secI)}
                      />
                    )}
                    <LegacyWBIcon
                      className="edit-stuff"
                      name="edit"
                      onClick={() => startEditSection(s, secI)}
                    />
                  </>
                )}
              </div>
              {s.tags.map((t, tI) => (
                <React.Fragment key={t.name}>
                  <div
                    className={classNames('tag-list-item', {
                      active: activeTag === t.name,
                    })}>
                    {t.subtags != null && (
                      <div
                        className={classNames('expand-tag', {
                          expanded: expandedTags[t.name],
                        })}
                        onClick={() => toggleExpandTag(t.name)}
                      />
                    )}
                    <Link to={galleryPath(tagSections, t.name, sortOrder)}>
                      {t.name}
                    </Link>
                    {showFreshnessPills &&
                      newPostsByTagKey[`${s.name}/${t.name}`] > 0 && (
                        <div className="fresh-pill">
                          {newPostsByTagKey[`${s.name}/${t.name}`]}
                        </div>
                      )}
                    {admin && (
                      <>
                        <LegacyWBIcon
                          className="edit-stuff add-tag"
                          name="plus"
                          onClick={() =>
                            startEditStuff(newGalleryTag(), [secI, tI, -1])
                          }
                        />
                        {tI > 0 && (
                          <LegacyWBIcon
                            className="edit-stuff move-up"
                            name="up-arrow"
                            onClick={() => moveStuffUp([secI, tI])}
                          />
                        )}
                        {tI < s.tags.length - 1 && (
                          <LegacyWBIcon
                            className="edit-stuff move-down"
                            name="down-arrow"
                            onClick={() => moveStuffDown([secI, tI])}
                          />
                        )}
                        <LegacyWBIcon
                          className="edit-stuff"
                          name="edit"
                          onClick={() => startEditStuff(t, [secI, tI])}
                        />
                      </>
                    )}
                  </div>
                  {t.subtags != null &&
                    t.subtags.length > 0 &&
                    expandedTags[t.name] && (
                      <div style={{paddingLeft: 24}}>
                        {t.subtags.map((st, stI) => (
                          <div
                            className={classNames('tag-list-item', {
                              active: activeTag === st.name,
                            })}
                            key={st.name}>
                            <Link
                              to={galleryPath(tagSections, st.name, sortOrder)}>
                              {st.name}
                            </Link>
                            {showFreshnessPills &&
                              newPostsByTagKey[`${s.name}/${st.name}`] > 0 && (
                                <div className="fresh-pill">
                                  {newPostsByTagKey[`${s.name}/${st.name}`]}
                                </div>
                              )}
                            {admin && (
                              <>
                                {stI > 0 && (
                                  <LegacyWBIcon
                                    className="edit-stuff move-up"
                                    name="up-arrow"
                                    onClick={() => moveStuffUp([secI, tI, stI])}
                                  />
                                )}
                                {stI < t.subtags!.length - 1 && (
                                  <LegacyWBIcon
                                    className="edit-stuff move-down"
                                    name="down-arrow"
                                    onClick={() =>
                                      moveStuffDown([secI, tI, stI])
                                    }
                                  />
                                )}
                                <LegacyWBIcon
                                  className="edit-stuff"
                                  name="edit"
                                  onClick={() =>
                                    startEditStuff(st, [secI, tI, stI])
                                  }
                                />
                              </>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                </React.Fragment>
              ))}
            </div>
          </React.Fragment>
        ))}
        {admin && (
          <div className="add-section">
            <LegacyWBIcon
              className="edit-stuff"
              name="plus"
              onClick={() =>
                startEditSection(newGalleryTagSection(), sections.length, true)
              }
            />
          </div>
        )}
      </div>
    );
  },
  {id: 'TagList'}
);

interface ExtraStuffProps {
  sections: ExtraStuffSection[];
  onSetSections(sections: ExtraStuffSection[]): void;
}

const ExtraStuffList: React.FC<ExtraStuffProps> = makeComp(
  ({sections, onSetSections}) => {
    const [editingStuff, setEditingStuff] = useState<ExtraStuff | null>(null);
    const [editingStuffIndices, setEditingStuffIndices] = useState<
      [number, number] | null
    >(null);
    const editingStuffValid =
      editingStuff != null
        ? Boolean(editingStuff.name && editingStuff.link)
        : false;

    const admin = useIsGalleryAdmin();

    const editStuff = (prop: keyof ExtraStuff, val: string) =>
      setEditingStuff(stuff => {
        if (stuff == null) {
          return null;
        }
        return produce(stuff, draft => {
          (draft as Struct)[prop] = val;
        });
      });

    const startEditStuff = (stuff: ExtraStuff, is: [number, number]) => {
      setEditingStuff(stuff);
      setEditingStuffIndices(is);
    };
    const stopEditStuff = () => {
      setEditingStuff(null);
      setEditingStuffIndices(null);
    };

    const saveStuff = () => {
      if (editingStuff == null || editingStuffIndices == null) {
        return;
      }

      onSetSections(
        produce(sections, draft => {
          const [i, j] = editingStuffIndices!;
          const section = draft[i];
          if (j === -1) {
            section.stuff.push(editingStuff);
          } else {
            section.stuff[j] = editingStuff;
          }
        })
      );

      stopEditStuff();
    };

    const deleteStuff = () => {
      if (
        editingStuff == null ||
        editingStuffIndices == null ||
        editingStuffIndices[1] === -1
      ) {
        return;
      }

      onSetSections(
        produce(sections, draft => {
          const [i, j] = editingStuffIndices!;
          const section = draft[i];
          section.stuff.splice(j, 1);
        })
      );

      stopEditStuff();
    };

    return (
      <div className="extra-stuff">
        {editingStuff != null && (
          <Modal open onClose={stopEditStuff} className="edit-event-modal">
            <Modal.Content>
              <Form>
                <div className="edit-event-modal-field">
                  <div className="edit-event-modal-label">Name</div>
                  <TextArea
                    className="edit-event-modal-input"
                    value={editingStuff.name}
                    onChange={(e, {value}) =>
                      editStuff('name', value as string)
                    }
                  />
                </div>
                {'time' in editingStuff && (
                  <div className="edit-event-modal-field">
                    <div className="edit-event-modal-label">Time</div>
                    <Input
                      className="edit-event-modal-input"
                      value={editingStuff.time}
                      onChange={(e, {value}) => editStuff('time', value)}
                    />
                  </div>
                )}
                <div className="edit-event-modal-field">
                  <div className="edit-event-modal-label">Link</div>
                  <Input
                    className="edit-event-modal-input"
                    value={editingStuff.link}
                    onChange={(e, {value}) => editStuff('link', value)}
                  />
                </div>
              </Form>
            </Modal.Content>
            <Modal.Actions>
              {editingStuffIndices?.[1] !== -1 && (
                <Button
                  className="edit-event-modal-delete"
                  onClick={deleteStuff}>
                  Delete
                </Button>
              )}
              <Button onClick={stopEditStuff}>Cancel</Button>
              <Button primary disabled={!editingStuffValid} onClick={saveStuff}>
                Confirm
              </Button>
            </Modal.Actions>
          </Modal>
        )}
        {sections.map((s, secI) => (
          <div key={s.name} className="extra-stuff-section">
            <div className="extra-stuff-header">
              {s.name}
              {admin && (
                <LegacyWBIcon
                  className="edit-stuff"
                  name="plus"
                  onClick={() => startEditStuff(newExtraStuff(), [secI, -1])}
                />
              )}
            </div>
            {s.stuff.map((st, stI) => (
              <div className="extra-stuff-item" key={st.name}>
                <a href={st.link} target="_blank" rel="noreferrer noopener">
                  <span className="extra-stuff-name">{st.name}</span>
                  {st.time && (
                    <>
                      {st.time}
                      <span className="extra-stuff-event-rsvp">
                        RSVP <Icon name="arrow right" />
                      </span>
                    </>
                  )}
                </a>
                {admin && (
                  <LegacyWBIcon
                    className="edit-stuff"
                    name="edit"
                    onClick={() => startEditStuff(st, [secI, stI])}
                  />
                )}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  },
  {id: 'ExtraStuffList'}
);

interface ReportCardTagListProps {
  tags: string[];
}

const ReportCardTagList: React.FC<ReportCardTagListProps> = makeComp(
  ({tags}) => {
    const [scrollDistance, setScrollDistance] = useState(0);
    const divEl = useRef<HTMLDivElement>(null);
    const spanEl = useRef<HTMLSpanElement>(null);

    useEffect(() => {
      const calculateScrollDistance = () => {
        if (divEl.current == null || spanEl.current == null) {
          return;
        }
        setScrollDistance(
          Math.max(0, spanEl.current.offsetWidth - divEl.current.clientWidth)
        );
      };
      const debounceHandleResize = _.debounce(calculateScrollDistance, 500);

      calculateScrollDistance();
      window.addEventListener('resize', debounceHandleResize);

      return () => {
        window.removeEventListener('resize', debounceHandleResize);
      };
    }, []);

    return (
      <div className="report-tags" ref={divEl}>
        <span
          ref={spanEl}
          // These styles are only active on hover because this element
          // relies on a set of ":not(:hover) !important" rules
          style={{
            right: `${scrollDistance}px`,
            transition: `right ${scrollDistance / 125}s linear`,
          }}>
          {tags.join(', ')}
        </span>
      </div>
    );
  },
  {id: 'ReportCardTagList'}
);
